import { ENVIRONMENT } from "src/js/util/environment";
import { useMemo } from "react";
import { Nullable } from "src/js/types/Nullable";
import { PlanningApplication, PlanningApplicationSchema } from "react-migration/domains/planning/types";
import { ZodError } from "zod";
import useSWR from "swr";

import { SelectionGeometry } from "src/js/stores/map/store";
import { negativeBuffer } from "react-migration/layouts/map/Multilayer/geometry_utilities";
import { useOnlineStatus } from "react-migration/domains/nav/hooks/useOnlineStatus";

type RawApplication = Partial<PlanningApplication> & { boundary: string };

function parseApplications(data: RawApplication[] = []) {
  const applications: PlanningApplication[] = [];
  const invalidApplications: {
    input: Partial<PlanningApplication>;
    error: ZodError<PlanningApplication>;
  }[] = [];

  data
    .map((application) => ({
      input: application,
      parsedApplication: PlanningApplicationSchema.safeParse(application),
    }))
    .forEach(({ input, parsedApplication }) => {
      if (parsedApplication.success) {
        applications.push(parsedApplication.data);
      } else {
        invalidApplications.push({ input, error: parsedApplication.error });
      }
    });

  return [applications, invalidApplications] as const;
}

type PlanningApplicationFetcherArgs = [string, Nullable<SelectionGeometry>];
type PlanningApplicationFetcherResponse = Promise<{ applications: RawApplication[] }>;

function planningApplicationFetcher([
  url,
  geometry,
]: PlanningApplicationFetcherArgs): PlanningApplicationFetcherResponse {
  return fetch(url, {
    method: "POST",
    body: JSON.stringify({ geometry: negativeBuffer(geometry) }),
    headers: {
      "Content-Type": "application/json",
    },
    mode: "cors",
  }).then((response) => response.json());
}

export function usePlanningApplicationsByGeometry(geometry: Nullable<SelectionGeometry>, disableRevalidate = false) {
  const isOnline = useOnlineStatus();
  const options = disableRevalidate
    ? {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false,
      }
    : {};

  const { data, error, isLoading } = useSWR(
    [`${ENVIRONMENT.PLANNING_SERVICE_URL}/planning-applications/within/v2`, geometry],
    planningApplicationFetcher,
    {
      ...options,
      isPaused: () => !isOnline,
    }
  );

  const parsedPlanningApplications = useMemo(() => {
    const [parsedApplications, invalidApplications] = parseApplications(data?.applications);

    for (const { input, error } of invalidApplications) {
      console.error(`Invalid Planning Application response: ${input?.id}`, { error });
    }
    return parsedApplications;
  }, [data?.applications]);

  return {
    data: parsedPlanningApplications,
    loading: isLoading,
    error: !data && error ? error : null, // only surface error if we have no valid data (swr returns previous valid data)
  };
}
